// DataComponent.tsx
import { useEffect, useState } from 'react';
import { useDataContext } from './Contexts/DataContext';
import { ComboBox, Item, Key, Text } from '@adobe/react-spectrum';
import { ProgressCircle } from '@adobe/react-spectrum';
import { Flex } from '@adobe/react-spectrum';
import { DataComponentProps } from './DataComponentProps';
import { useTranslation } from 'react-i18next';

export interface ActivityContextFilter {
  parent_project_id?: number;
  parent_activity_id?: number;
  parent_folder_id?: number;
}

interface ActivityDropDownProps extends Omit<DataComponentProps, 'onValueChange'> {
  parent_folder_id?: number;
  parent_project_id?: number;
  parent_activity_id?: number;
  related_to_activity_type?: number;
  contextFilter?: ActivityContextFilter;
  returnFullObject?: boolean;
  onValueChange?: (value: string | Record<string, any>, input?: string) => void;
}
const ActivityDropDown: React.FC<ActivityDropDownProps> = ({
  parent_folder_id: parentFolderIdProp,
  parent_project_id: parentProjectIdProp,
  parent_activity_id: parentActivityIdProp,
  related_to_activity_type,
  contextFilter,
  compKey,
  label,
  width,
  isRequired,
  onValueChange,
  value: dvalue,
  returnFullObject = false,
}) => {
  const { t } = useTranslation();
  const _t = (key: string) => t(`newContextDialog.dataComponents.${key}`, { ns: 'finder' });
  const message = _t('is_required').split('[content]').join(label);
  const [errorMessage, setErrorMessage] = useState(isRequired ? message : '');
  const [value, setValue] = useState<Key | null>('');
  const [input, setInput] = useState('');
  const { getActivityData } = useDataContext();

  const parent_project_id = contextFilter?.parent_project_id ?? parentProjectIdProp;
  const parent_activity_id = contextFilter?.parent_activity_id ?? parentActivityIdProp;
  const parent_folder_id = contextFilter?.parent_folder_id ?? parentFolderIdProp;

  const { data, error, loading } = getActivityData({ parent_folder_id, parent_project_id, parent_activity_id, related_to_activity_type });

  useEffect(() => {
    if (dvalue !== undefined && dvalue !== null) {
      const keyForDisplay =
        typeof dvalue === 'object' && dvalue !== null && 'id' in dvalue
          ? String((dvalue as { id: string | number }).id)
          : dvalue !== 'undefined'
            ? String(dvalue)
            : '';
      setValue(keyForDisplay);
      if (isRequired && !keyForDisplay) setErrorMessage(message);
      else setErrorMessage('');
    }
  }, [dvalue, message, isRequired]);

  useEffect(() => {
    if (data.length === 1 && !value && !dvalue) {
      setSelection(String(data[0].id));
    }
  }, [data]);
  
  const setSelection = (p_value: string | Key | null) => {
    const keyStr = p_value ? String(p_value) : '';
    if (onValueChange) {
      if (returnFullObject && keyStr) {
        const fullItem = data.find((i) => String(i.id) === keyStr);
        onValueChange(fullItem ?? keyStr, input);
      } else {
        onValueChange(keyStr, input);
      }
    }
    setValue(keyStr);

    if (isRequired && !keyStr) setErrorMessage(message);
    else setErrorMessage('');
  };
  const onSelectionChange = (p_value: Key | null) => {
    setSelection(p_value ? p_value : null);
  };

  const onInputChange = (p_value: string) => {
    setInput(p_value);
    if(isRequired && !p_value) 
      setErrorMessage(message);
    else 
      setErrorMessage('');
  }
  
  if (loading) {
    return (
      <Flex alignItems="center" gap="size-100">
        <ProgressCircle aria-label="Loading…" isIndeterminate size="S" />
        <Text>{t('newContextDialog.loading', { ns: 'finder' }) || 'Loading…'}</Text>
      </Flex>
    );
  }
  if (error) return <div>Error: {error}</div>;

  return (
    <ComboBox key={compKey} 
        label={label} 
        width={width} 
        isRequired={isRequired} 
        selectedKey={value}
        onSelectionChange={onSelectionChange} 
        onInputChange={onInputChange}
        validationState={errorMessage ? 'invalid' : undefined}
        errorMessage={errorMessage}
        >
      {data.map((item) => (
        <Item key={item.id} textValue={item.title}>
          <Text>{item.title}{item.refId ? ` (${item.refId})` : ''}</Text>
          {item.parent?.title != null && item.parent.title !== '' && (
            <Text slot="description">{item.parent.title}</Text>
          )}
        </Item>
      ))}
    </ComboBox>
  );
};

export default ActivityDropDown;
